import React, { createContext, useContext, useMemo } from 'react';
import { MinusIcon, PlusIcon } from '@heroicons/react/24/outline';
import useQuoteCardScales, { CardScaleMap, FONT_SCALES, stepFontScale } from './useQuoteCardScales';

export interface QuoteCardScaleContextValue {
  /** Current per-card scale map (card identity → scale; missing = 100%). */
  scales: CardScaleMap;
  /** Persist a card's scale (merged, per-user, across quotes). */
  setScale: (key: string, value: number) => void;
}

/**
 * Supplies per-card font scales to `Card`s rendered inside the Quotes drawer.
 * Default `null` means "no provider" — `Card` treats that as the feature being
 * OFF (no control, no zoom), which is exactly what the standalone
 * QuoteDetailPage / GroupedQuoteDetailPage want since they don't wrap this
 * provider.
 */
export const QuoteCardScaleContext = createContext<QuoteCardScaleContextValue | null>(null);

/**
 * Wrap the Quote drawer body in this so its cards get the hover font-scaling
 * control. Owns the persistence via useQuoteCardScales.
 */
export const QuoteCardScaleProvider: React.FC<{ children: React.ReactNode }> = ({ children }) => {
  const { scales, setScale } = useQuoteCardScales();
  const value = useMemo<QuoteCardScaleContextValue>(() => ({ scales, setScale }), [scales, setScale]);
  return <QuoteCardScaleContext.Provider value={value}>{children}</QuoteCardScaleContext.Provider>;
};

/**
 * Read the active scale + a setter for one card from the provider. `fontScalable`
 * is false when the card has no `scaleKey` OR there's no provider (standalone
 * pages) — in which case the card renders normally with no control and no zoom.
 * `bodyStyle` is the inline `zoom` to spread onto the body the card wants scaled.
 */
export function useCardFontScale(scaleKey?: string): {
  fontScalable: boolean;
  scale: number;
  setScale: (value: number) => void;
  bodyStyle: React.CSSProperties | undefined;
} {
  const ctx = useContext(QuoteCardScaleContext);
  const fontScalable = Boolean(scaleKey && ctx);
  const scale = fontScalable ? (ctx!.scales[scaleKey!] ?? 1) : 1;
  const setScale = (value: number) => { if (scaleKey) ctx?.setScale(scaleKey, value); };
  const bodyStyle = fontScalable && scale !== 1 ? { zoom: scale } : undefined;
  return { fontScalable, scale, setScale, bodyStyle };
}

/**
 * The hover-revealed `− % +` font-scaling control shown in a card header.
 * Renders null unless the card is font-scalable (has a `scaleKey` AND sits
 * inside a provider). At 100% it hides until the card is hovered/focused; once
 * scaled it stays visible as an indicator + a way back. The HOST card must carry
 * the `group/card-scale` class (so the hover reveal works) and apply the
 * matching `useCardFontScale().bodyStyle` (+ optional `data-font-scale`) to the
 * body it wants zoomed — this component only renders the control.
 */
export const FontScaleControl: React.FC<{ scaleKey?: string; title: string }> = ({ scaleKey, title }) => {
  const { fontScalable, scale, setScale } = useCardFontScale(scaleKey);
  if (!fontScalable) return null;
  return (
    <div
      data-testid="card-font-scale"
      className={[
        'flex items-center gap-0.5 transition-opacity',
        scale !== 1 ? 'opacity-100' : 'opacity-0 group-hover/card-scale:opacity-100 focus-within:opacity-100',
      ].join(' ')}
    >
      <button
        type="button"
        aria-label={`Decrease ${title} font size`}
        disabled={scale <= FONT_SCALES[0]}
        onClick={(e) => { e.stopPropagation(); setScale(stepFontScale(scale, -1)); }}
        className="inline-flex items-center justify-center h-4 w-4 rounded text-slate-400 hover:text-slate-600 dark:hover:text-slate-300 disabled:opacity-30 disabled:cursor-default"
      >
        <MinusIcon strokeWidth={2.5} className="h-3 w-3" />
      </button>
      <span className="text-[0.625rem] tabular-nums text-slate-400 dark:text-slate-500 w-7 text-center select-none">
        {Math.round(scale * 100)}%
      </span>
      <button
        type="button"
        aria-label={`Increase ${title} font size`}
        disabled={scale >= FONT_SCALES[FONT_SCALES.length - 1]}
        onClick={(e) => { e.stopPropagation(); setScale(stepFontScale(scale, 1)); }}
        className="inline-flex items-center justify-center h-4 w-4 rounded text-slate-400 hover:text-slate-600 dark:hover:text-slate-300 disabled:opacity-30 disabled:cursor-default"
      >
        <PlusIcon strokeWidth={2.5} className="h-3 w-3" />
      </button>
    </div>
  );
};
